import { Button, Card, useToast } from '@citizen-walk/shared-ui';
import {
  getModeLabel,
  getModeTemplate,
  parseAnalysisDisplayRules,
  type AnalysisAxisRule,
  type AnalysisDisplayConfig,
  type AnalysisDisplayRules,
  type DistrictMode,
  type FieldDef,
  type PostDetailFieldEntry,
  type PostDetailFieldRef,
  type ScoreColumnConfig,
  type ScoreDisplayConfig,
} from '@citizen-walk/shared-types';
import { useMemo, useState } from 'react';
import { archiveMaster, upsertMaster, useAnalysisDisplayConfigs } from './useMasters';
import { MasterHistoryView } from './MasterHistoryView';
import { MasterImportExportButtons, RowExportButton } from './MasterImportExportButtons';
import { stripSystemFields } from './jsonImportExport';
import { ModeSelectField } from './components/ModeSelectField';
// 使用状況の pill バッジ (AI 出力フォーマット M3 一覧と共用)。
import { UsageBadges, type UsageBadgeItem } from './components/UsageBadges';
// 別名で保存 (複製) / ドキュメント ID の検証 / 使用状況の集計は M1・M3 統合エディタと同じ純関数を再利用する。
import {
  duplicateDraft,
  groupAnalysisDisplayConfigUsage,
  uniqueSlug,
  validateDocumentId,
  type DisplayConfigUsage,
} from './aiAnalysisEditorHelpers';
// 使用状況 (経路1): 地区の直接紐付け (District.analysisDisplayConfigId)。
import { useDistricts } from '../districts-admin';
// 使用状況 (経路2): モードプロファイル (M10) の「新規地区への推奨設定」。
import { useModeProfiles } from './useModeProfiles';

/**
 * M5 analysisDisplayConfigs 管理画面 (docs/04 §3-1 M5、Sprint AU で form 化)。
 *
 * Sprint AU で `displayRules: unknown` → `AnalysisDisplayRules` 正式型化を受け、
 * 必須 4 field を form で編集可能にした:
 *   - matrix.defaultVAxis
 *   - map.defaultColorAxis
 *   - heatmap.defaultWeightMode + heatmap.weightField
 *   - aiDialogueSettingsId
 *
 * AW1 (docs/108 Critical #1,#2) で old2 admin 同等まで form 化:
 *   - matrix.defaultHAxis / vAxisRule / hAxisRule / additionalAxisRules (bucketMethod + customRanges 行エディタ)
 *   - map.defaultSizeAxis / defaultTileType / defaultMarkerMode / defaultHeatmapMode
 *   - postDetailScores (スコアカード列、最大3列)
 *   - 軸キー系は <datalist> 候補付き input (config.mode の modeTemplate 由来) で「手打ち」を解消
 * 残りの補助 field (map.defaultCenter / heatmap.defaultRadius など) は JSON 編集 textarea で扱う。
 *
 * 保存時のロジック:
 *   1. JSON textarea を parse (空なら空 object)
 *   2. form の 4 field を JSON 結果に **上書き** (form が source of truth)
 *   3. parseAnalysisDisplayRules で sanitize して Firestore に書込
 *
 * → 既存 master の補助 field (map.defaultZoom 等) は維持される + form 4 field は確実に正規化。
 */

const COLLECTION_NAME = 'analysisDisplayConfigs';

const INPUT_CLASS =
  'w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-400';

interface FormDraft {
  id: string;
  displayName: string;
  mode: DistrictMode;
  /** matrix.defaultVAxis (空文字なら未設定)。 */
  matrixDefaultVAxis: string;
  /** AW1: matrix.defaultHAxis (空文字なら未設定)。 */
  matrixDefaultHAxis: string;
  /** AW1: matrix.vAxisRule (bucketMethod / customRanges、null = 未設定)。 */
  matrixVAxisRule: AnalysisAxisRule | null;
  /** AW1: matrix.hAxisRule。 */
  matrixHAxisRule: AnalysisAxisRule | null;
  /** AW1: matrix.additionalAxisRules (軸切替時に適用する追加ルール)。 */
  matrixAdditionalAxisRules: AnalysisAxisRule[];
  /**
   * MIG-1 M-A: matrix.scoreFilter (旧 defaultScoreFilter)。
   * カンマ区切り number 入力 (例: "-3,-2,-1,1,2,3")、空文字なら未設定。
   */
  matrixScoreFilter: string;
  /** MIG-1 M-B: matrix.imageSize (px、空文字なら未設定)。 */
  matrixImageSize: string;
  /** MIG-1 M-B: matrix.showTitle (旧 defaultShowTitle、'' = 未指定 / 'true' / 'false')。 */
  matrixShowTitle: '' | 'true' | 'false';
  /** MIG-1 M-B: matrix.sortField (例: 'score', 'createdAt'、空文字なら未設定)。 */
  matrixSortField: string;
  /** MIG-1 M-B: matrix.sortOrder ('' / 'asc' / 'desc')。 */
  matrixSortOrder: '' | 'asc' | 'desc';
  /** map.defaultColorAxis (空文字なら未設定)。 */
  mapDefaultColorAxis: string;
  /** AW1: map.defaultSizeAxis (空文字なら未設定)。 */
  mapDefaultSizeAxis: string;
  /** AW1: map.defaultTileType ('' = 未指定 / 'osm' / 'pmtiles')。 */
  mapDefaultTileType: '' | 'osm' | 'pmtiles';
  /** AW1: map.defaultMarkerMode ('' = 未指定 / 'true' / 'false')。 */
  mapDefaultMarkerMode: '' | 'true' | 'false';
  /** AW1: map.defaultHeatmapMode ('' = 未指定 / 'true' / 'false')。 */
  mapDefaultHeatmapMode: '' | 'true' | 'false';
  /** heatmap.defaultWeightMode ('' = 未指定 / 'count' / 'score')。 */
  heatmapDefaultWeightMode: '' | 'count' | 'score';
  /** heatmap.weightField (defaultWeightMode='score' のとき意味あり、空文字なら未設定)。 */
  heatmapWeightField: string;
  /** Task D 動的化: heatmap.weightFormula (空文字なら未設定)。 */
  heatmapWeightFormula: string;
  /** reviewer follow-up ⚠️-5: heatmap.secondaryWeightField (空文字なら未指定 → HealthScore fallback)。 */
  heatmapSecondaryWeightField: string;
  /** aiDialogueSettingsId (空文字なら未設定)。 */
  aiDialogueSettingsId: string;
  /**
   * 動的管理拡張: postDetailFields の編集 (legacy 6 種 string + custom ref object 混在可)。
   * 空配列なら書込まない (default 順を使う)。
   */
  postDetailFields: PostDetailFieldEntry[];
  /** Task D 動的化: score sub-rule の form field 群 (空文字なら未設定)。 */
  scoreXKey: string;
  scoreYKey: string;
  scoreXLabel: string;
  scoreYLabel: string;
  /** reviewer follow-up ⚠️-3: 値域 4 field (空文字なら未指定 fallback で -3..+3)。 */
  scoreXMin: string;
  scoreXMax: string;
  scoreYMin: string;
  scoreYMax: string;
  scoreQuadrantTopRight: string;
  scoreQuadrantTopLeft: string;
  scoreQuadrantBottomRight: string;
  scoreQuadrantBottomLeft: string;
  /** AW1 (docs/108 Critical #2): 投稿詳細スコアカード列 (最大3列、null = 未設定)。 */
  postDetailScores: ScoreDisplayConfig | null;
  /** 他の field を保持する JSON テキスト (form 4 field + postDetailFields 以外の入れ子)。 */
  jsonText: string;
}

const POST_DETAIL_LEGACY_TYPES = [
  'description',
  'emotions',
  'emotionText',
  'emotionReason',
  'categoryCodes',
  'categoryReason',
] as const;

const EMPTY_DRAFT: FormDraft = {
  id: '',
  displayName: '',
  mode: 'ecw',
  matrixDefaultVAxis: '',
  matrixDefaultHAxis: '',
  matrixVAxisRule: null,
  matrixHAxisRule: null,
  matrixAdditionalAxisRules: [],
  matrixScoreFilter: '',
  matrixImageSize: '',
  matrixShowTitle: '',
  matrixSortField: '',
  matrixSortOrder: '',
  mapDefaultColorAxis: '',
  mapDefaultSizeAxis: '',
  mapDefaultTileType: '',
  mapDefaultMarkerMode: '',
  mapDefaultHeatmapMode: '',
  heatmapDefaultWeightMode: '',
  heatmapWeightField: '',
  heatmapWeightFormula: '',
  heatmapSecondaryWeightField: '',
  aiDialogueSettingsId: '',
  postDetailFields: [],
  scoreXKey: '',
  scoreYKey: '',
  scoreXLabel: '',
  scoreYLabel: '',
  scoreXMin: '',
  scoreXMax: '',
  scoreYMin: '',
  scoreYMax: '',
  scoreQuadrantTopRight: '',
  scoreQuadrantTopLeft: '',
  scoreQuadrantBottomRight: '',
  scoreQuadrantBottomLeft: '',
  postDetailScores: null,
  jsonText: '',
};

/* ============================================================================
 * AW1 (docs/108 Critical #1): マトリクス軸ルールのフォームエディタ
 *   - old2 AiSettingsMasterPage の AxisSelect / renderAxisRule / BUCKET_LABELS 相当を移植。
 *   - 軸キーは <datalist> 候補付き input (ドロップダウン提示 + 候補外も入力可)。地区横断 master の
 *     ため、軸候補は config.mode の modeTemplate から導出 (master 軸 + postField 軸)。
 *   - bucketMethod は v2 AnalysisAxisRule の 5 種 (old2 の each-value は v2 union 無し→ 6 段階分けは
 *     custom-ranges で表現)。
 * ========================================================================== */

type BucketMethod = NonNullable<AnalysisAxisRule['bucketMethod']>;

const BUCKET_METHODS: { value: BucketMethod; label: string }[] = [
  { value: 'auto', label: '自動 (軸タイプに応じた標準)' },
  { value: 'by-name', label: '名称で分類 (カテゴリ名・選択肢名)' },
  { value: 'mean-split', label: '平均値で 2 分割' },
  { value: 'positive-negative', label: '正負で 2 分割' },
  { value: 'custom-ranges', label: 'カスタム範囲 (各段階を min/max で指定)' },
];

/**
 * config.mode から軸候補を導出 (master 軸 + postField 軸 + 連続値キー)。
 * 軸キーは自由入力の datalist **候補提示のみ** (地区の実マスタ・実 M3 に依存するため)。
 *
 * 【廃止予定 fallback を含む】mode 文字列分岐 (walker→gql / bousai→hzr) と
 * getModeTemplate (@deprecated) の postField 候補は legacy 地区・組込モード向けの
 * 候補提示。新規 custom mode では M3 classificationAxes の軸キー
 * (`<axisKey>.major` / `<axisKey>.categoryCd`) を自由入力するか、ModeProfile の
 * postFields を District に転写した値が実際の軸になる (候補に無くても動作は同じ)。
 * 撤去条件: 軸候補を M3/M2 実マスタから動的取得する改善 (issue 化予定) の実装時。
 */
function buildModeAxisOptions(mode: DistrictMode): { key: string; label: string }[] {
  const out: { key: string; label: string }[] = [];
  const seen = new Set<string>();
  const add = (key: string, label: string) => {
    if (key && !seen.has(key)) {
      seen.add(key);
      out.push({ key, label });
    }
  };
  // 典型的な master 軸 (templateVar 由来)。地区の実マスタに依存するため候補提示のみ。
  add('emotion.major', '感情 (大分類)');
  add('emotion.category', '感情 (詳細)');
  add('category.major', 'カテゴリ (大分類)');
  add('category.categoryCd', 'カテゴリ (詳細コード)');
  if (mode === 'walker') {
    add('gql.major', 'GQL (大分類)');
    add('gql.categoryCd', 'GQL (詳細)');
  }
  if (mode === 'bousai' || mode === 'kids-bousai') {
    add('hzr.major', '災害 (大分類)');
    add('hzr.categoryCd', '災害 (詳細)');
  }
  // postField 軸 (rating-scale / select / multiselect)。custom-ranges 軸ルール用に bare キーも候補化。
  // 【廃止予定 fallback】getModeTemplate は組込モードの静的テンプレのみ返す (custom mode は
  // BOUSAI_TEMPLATE に落ちる) — 候補提示専用なので動作影響はないが、実データ駆動化が望ましい。
  const tpl = getModeTemplate(mode);
  for (const f of tpl.dataSettings.postFields) {
    const usable =
      f.useAsAnalysisAxis ??
      (f.type === 'select' || f.type === 'rating-scale' || f.type === 'multiselect');
    if (!usable) continue;
    add(`postField:${f.key}`, `${f.label} 〔postField:${f.key}〕`);
    if (f.type === 'rating-scale' || f.type === 'number') {
      add(f.key, `${f.label} 連続値 〔${f.key}〕`);
    }
  }
  return out;
}

const AXIS_INPUT_CLASS =
  'w-full px-2 py-1.5 text-sm border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-400';

/** 軸キー入力 (datalist 候補付き)。候補から選択 or 自由入力。 */
function AxisInput({
  id,
  label,
  value,
  onChange,
  options,
  placeholder,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { key: string; label: string }[];
  placeholder?: string;
}) {
  const listId = `${id}-list`;
  return (
    <div>
      <label className="block text-xs text-slate-600 mb-1" htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        list={listId}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={AXIS_INPUT_CLASS}
        placeholder={placeholder ?? '軸キー (候補から選択 or 入力、空で未指定)'}
      />
      <datalist id={listId}>
        {options.map((o) => (
          <option key={o.key} value={o.key}>
            {o.label}
          </option>
        ))}
      </datalist>
    </div>
  );
}

/** custom-ranges の各段階 (label / min / max) を行エディタで編集。 */
function CustomRangesEditor({
  ranges,
  onChange,
}: {
  ranges: { label: string; min: number; max: number }[];
  onChange: (next: { label: string; min: number; max: number }[]) => void;
}) {
  const update = (i: number, patch: Partial<{ label: string; min: number; max: number }>) => {
    onChange(ranges.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  };
  return (
    <div className="mt-2 bg-white rounded border border-slate-200 p-2">
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs font-semibold text-slate-600">カスタム範囲 (各段階)</span>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() =>
              onChange([
                { label: '+3', min: 3, max: 3 },
                { label: '+2', min: 2, max: 2 },
                { label: '+1', min: 1, max: 1 },
                { label: '-1', min: -1, max: -1 },
                { label: '-2', min: -2, max: -2 },
                { label: '-3', min: -3, max: -3 },
              ])
            }
            className="text-[11px] text-primary-600 hover:underline"
          >
            6 段階 (+3〜-3) を生成
          </button>
          <button
            type="button"
            onClick={() => onChange([...ranges, { label: '', min: 0, max: 0 }])}
            className="text-[11px] text-primary-600 hover:underline"
          >
            ＋ 範囲を追加
          </button>
        </div>
      </div>
      {ranges.length === 0 ? (
        <p className="text-[11px] text-slate-400 py-1">
          範囲が未設定です。「6 段階を生成」か「範囲を追加」で段階を作成してください。
        </p>
      ) : (
        <div className="flex flex-col gap-1">
          <div className="grid grid-cols-[1fr_70px_70px_28px] gap-1 text-[10px] text-slate-400 px-0.5">
            <span>表示ラベル</span>
            <span>min</span>
            <span>max</span>
            <span />
          </div>
          {ranges.map((r, i) => (
            <div key={i} className="grid grid-cols-[1fr_70px_70px_28px] gap-1 items-center">
              <input
                type="text"
                value={r.label}
                onChange={(e) => update(i, { label: e.target.value })}
                className="px-2 py-1 text-xs border border-slate-300 rounded"
                placeholder="例: +3"
              />
              <input
                type="number"
                value={r.min}
                onChange={(e) => update(i, { min: Number(e.target.value) })}
                className="px-1 py-1 text-xs border border-slate-300 rounded"
              />
              <input
                type="number"
                value={r.max}
                onChange={(e) => update(i, { max: Number(e.target.value) })}
                className="px-1 py-1 text-xs border border-slate-300 rounded"
              />
              <button
                type="button"
                onClick={() => onChange(ranges.filter((_, idx) => idx !== i))}
                className="text-slate-400 hover:text-danger-600 text-sm"
                aria-label="この範囲を削除"
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** 1 軸ルール (対象軸 + bucketMethod + custom-ranges) のエディタ。axisKey 空で「未設定」。 */
function AxisRuleEditor({
  idPrefix,
  label,
  rule,
  onChange,
  options,
  onRemove,
}: {
  idPrefix: string;
  label: string;
  rule: AnalysisAxisRule | null;
  onChange: (next: AnalysisAxisRule | null) => void;
  options: { key: string; label: string }[];
  onRemove?: () => void;
}) {
  const method: BucketMethod = rule?.bucketMethod ?? 'auto';
  return (
    <div className="border border-slate-200 rounded p-3 bg-slate-50 mb-2">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-semibold text-slate-700">{label}</span>
        {onRemove && (
          <button
            type="button"
            onClick={onRemove}
            className="text-[11px] text-slate-400 hover:text-danger-600"
          >
            ✕ このルールを削除
          </button>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <AxisInput
          id={`${idPrefix}-axis`}
          label="対象軸"
          value={rule?.axisKey ?? ''}
          onChange={(v) =>
            onChange(
              v
                ? {
                    axisKey: v,
                    bucketMethod: method,
                    customRanges: rule?.customRanges,
                    includeEmpty: rule?.includeEmpty,
                  }
                : null,
            )
          }
          options={options}
        />
        <div>
          <label className="block text-xs text-slate-600 mb-1" htmlFor={`${idPrefix}-method`}>
            分類方法 (bucketMethod)
          </label>
          <select
            id={`${idPrefix}-method`}
            value={method}
            disabled={!rule?.axisKey}
            onChange={(e) =>
              onChange({
                axisKey: rule?.axisKey ?? '',
                bucketMethod: e.target.value as BucketMethod,
                customRanges: rule?.customRanges,
                includeEmpty: rule?.includeEmpty,
              })
            }
            className={`${AXIS_INPUT_CLASS} disabled:bg-slate-100 disabled:text-slate-400`}
          >
            {BUCKET_METHODS.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </select>
        </div>
      </div>
      {rule?.axisKey && method === 'custom-ranges' && (
        <CustomRangesEditor
          ranges={rule.customRanges ?? []}
          onChange={(ranges) =>
            onChange({
              axisKey: rule.axisKey,
              bucketMethod: 'custom-ranges',
              customRanges: ranges,
              includeEmpty: rule.includeEmpty,
            })
          }
        />
      )}
      {/* (2) 軸に値が無い投稿/AI認識物を「（該当なし）」バケットで表示するか (軸ごと設定)。
          既定軸 (景観の観点・対象物 等) にもこのチェックだけ付けたいときは、その軸を「対象軸」に
          選び分類方法は既定 (auto) のまま、本チェックを ON にすればよい。 */}
      {rule?.axisKey && (
        <label className="mt-2 flex items-center gap-2 text-xs text-slate-600">
          <input
            type="checkbox"
            checked={!!rule.includeEmpty}
            onChange={(e) => onChange({ ...rule, includeEmpty: e.target.checked })}
          />
          この軸の値が無い投稿/AI認識物を「（該当なし）」として表示する
        </label>
      )}
    </div>
  );
}

/* ============================================================================
 * AW1 (docs/108 Critical #2): 投稿詳細スコアカード列エディタ
 *   - old2 ScoreColumnEditor / CustomFormulaModal 相当 (最大3列)。
 *   - 各列: 名称 / 値の源泉 path / 抽出(all/first) / フィルタ式 / 集約 or 式 / 倍率 / 小数 / 単位。
 * ========================================================================== */

const SCORE_AGGREGATORS: { value: 'avg' | 'sum' | 'max' | 'min' | 'median'; label: string }[] = [
  { value: 'avg', label: '平均 (avg)' },
  { value: 'sum', label: '合計 (sum)' },
  { value: 'max', label: '最大 (max)' },
  { value: 'min', label: '最小 (min)' },
  { value: 'median', label: '中央値 (median)' },
];

const SCORE_MAX_COLUMNS = 3;

function makeEmptyScoreColumn(i: number): ScoreColumnConfig {
  return {
    id: `col${i}`,
    label: '',
    fieldPath: '',
    selectionMode: 'all',
    filter: { expr: '' },
    calculation: { kind: 'aggregate', aggregator: 'avg' },
  };
}

function ScoreColumnsEditor({
  value,
  onChange,
}: {
  value: ScoreDisplayConfig | null;
  onChange: (next: ScoreDisplayConfig | null) => void;
}) {
  const enabled = value?.enabled ?? false;
  const columns = value?.columns ?? [];
  const setColumns = (cols: ScoreColumnConfig[]) => onChange({ enabled, columns: cols });
  const updateCol = (i: number, patch: Partial<ScoreColumnConfig>) =>
    setColumns(columns.map((c, idx) => (idx === i ? { ...c, ...patch } : c)));
  return (
    <div className="border border-slate-200 rounded-md p-3 bg-white">
      <label className="flex items-center gap-2 text-xs font-bold text-slate-700 mb-2">
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) =>
            onChange({
              enabled: e.target.checked,
              columns: columns.length > 0 ? columns : [makeEmptyScoreColumn(0)],
            })
          }
        />
        投稿詳細スコアカード列 (postDetailScores) を有効化
      </label>
      <p className="text-[11px] text-slate-500 mb-2">
        投稿詳細モーダルに数値カード (最大 {SCORE_MAX_COLUMNS} 列) を表示。値の源泉 path は{' '}
        <code>extra.&lt;key&gt;</code> / <code>ai.&lt;mode&gt;.&lt;field&gt;.&lt;sub&gt;</code> /{' '}
        <code>ai.&lt;mode&gt;.classifications[].score</code> など。
      </p>
      {enabled &&
        columns.map((col, i) => {
          const isFormula = col.calculation.kind === 'formula';
          return (
            <div key={i} className="border border-slate-200 rounded p-2 bg-slate-50 mb-2">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[11px] font-semibold text-slate-600">列 #{i + 1}</span>
                <button
                  type="button"
                  onClick={() => setColumns(columns.filter((_, idx) => idx !== i))}
                  className="text-[11px] text-slate-400 hover:text-danger-600"
                >
                  ✕ 削除
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <input
                  type="text"
                  value={col.label}
                  onChange={(e) => updateCol(i, { label: e.target.value })}
                  className={AXIS_INPUT_CLASS}
                  placeholder="列名 (例: 総合スコア)"
                />
                <input
                  type="text"
                  value={col.fieldPath}
                  onChange={(e) => updateCol(i, { fieldPath: e.target.value })}
                  className={AXIS_INPUT_CLASS}
                  placeholder="値の源泉 path (式列は空でも可)"
                />
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2">
                <select
                  value={col.selectionMode}
                  onChange={(e) =>
                    updateCol(i, { selectionMode: e.target.value as 'all' | 'first' })
                  }
                  className={AXIS_INPUT_CLASS}
                  aria-label="抽出"
                >
                  <option value="all">配列全件 (all)</option>
                  <option value="first">先頭1件 (first)</option>
                </select>
                <input
                  type="text"
                  value={col.filter.expr}
                  onChange={(e) => updateCol(i, { filter: { expr: e.target.value } })}
                  className={AXIS_INPUT_CLASS}
                  placeholder="フィルタ式 (例: >=2、空=全件)"
                />
                <select
                  value={col.calculation.kind}
                  onChange={(e) =>
                    updateCol(
                      i,
                      e.target.value === 'formula'
                        ? { calculation: { kind: 'formula', formula: '' } }
                        : { calculation: { kind: 'aggregate', aggregator: 'avg' } },
                    )
                  }
                  className={AXIS_INPUT_CLASS}
                  aria-label="計算方法"
                >
                  <option value="aggregate">集約関数</option>
                  <option value="formula">カスタム式</option>
                </select>
                {isFormula ? (
                  <input
                    type="text"
                    value={col.calculation.kind === 'formula' ? col.calculation.formula : ''}
                    onChange={(e) =>
                      updateCol(i, { calculation: { kind: 'formula', formula: e.target.value } })
                    }
                    className={AXIS_INPUT_CLASS}
                    placeholder="式 (例: [A] - [B])"
                  />
                ) : (
                  <select
                    value={
                      col.calculation.kind === 'aggregate' ? col.calculation.aggregator : 'avg'
                    }
                    onChange={(e) =>
                      updateCol(i, {
                        calculation: {
                          kind: 'aggregate',
                          aggregator: e.target.value as 'avg' | 'sum' | 'max' | 'min' | 'median',
                          multiplier:
                            col.calculation.kind === 'aggregate'
                              ? col.calculation.multiplier
                              : undefined,
                        },
                      })
                    }
                    className={AXIS_INPUT_CLASS}
                    aria-label="集約関数"
                  >
                    {SCORE_AGGREGATORS.map((a) => (
                      <option key={a.value} value={a.value}>
                        {a.label}
                      </option>
                    ))}
                  </select>
                )}
              </div>
              <div className="grid grid-cols-3 gap-2 mt-2">
                <input
                  type="number"
                  step="any"
                  value={
                    col.calculation.kind === 'aggregate' && col.calculation.multiplier !== undefined
                      ? col.calculation.multiplier
                      : ''
                  }
                  disabled={isFormula}
                  onChange={(e) =>
                    updateCol(i, {
                      calculation: {
                        kind: 'aggregate',
                        aggregator:
                          col.calculation.kind === 'aggregate' ? col.calculation.aggregator : 'avg',
                        multiplier: e.target.value === '' ? undefined : Number(e.target.value),
                      },
                    })
                  }
                  className={`${AXIS_INPUT_CLASS} disabled:bg-slate-100`}
                  placeholder="倍率"
                />
                <input
                  type="number"
                  value={col.decimals ?? ''}
                  onChange={(e) =>
                    updateCol(i, {
                      decimals: e.target.value === '' ? undefined : Number(e.target.value),
                    })
                  }
                  className={AXIS_INPUT_CLASS}
                  placeholder="小数桁"
                />
                <input
                  type="text"
                  value={col.unit ?? ''}
                  onChange={(e) => updateCol(i, { unit: e.target.value || undefined })}
                  className={AXIS_INPUT_CLASS}
                  placeholder="単位 (例: 点)"
                />
              </div>
            </div>
          );
        })}
      {enabled && columns.length < SCORE_MAX_COLUMNS && (
        <button
          type="button"
          onClick={() => setColumns([...columns, makeEmptyScoreColumn(columns.length)])}
          className="text-[11px] text-primary-600 hover:underline"
        >
          ＋ スコア列を追加 ({columns.length}/{SCORE_MAX_COLUMNS})
        </button>
      )}
    </div>
  );
}

/**
 * 既存 master の displayRules から form 4 field を抜き、残りを JSON textarea 用に分離。
 *
 * matrix.defaultVAxis / map.defaultColorAxis / heatmap.defaultWeightMode + weightField /
 * aiDialogueSettingsId は form の方が source of truth なので JSON 側からは外す。
 */
function rulesToDraftPair(rules: AnalysisDisplayRules): {
  form: Pick<
    FormDraft,
    | 'matrixDefaultVAxis'
    | 'matrixDefaultHAxis'
    | 'matrixVAxisRule'
    | 'matrixHAxisRule'
    | 'matrixAdditionalAxisRules'
    | 'mapDefaultSizeAxis'
    | 'mapDefaultTileType'
    | 'mapDefaultMarkerMode'
    | 'mapDefaultHeatmapMode'
    | 'matrixScoreFilter'
    | 'matrixImageSize'
    | 'matrixShowTitle'
    | 'matrixSortField'
    | 'matrixSortOrder'
    | 'mapDefaultColorAxis'
    | 'heatmapDefaultWeightMode'
    | 'heatmapWeightField'
    | 'heatmapWeightFormula'
    | 'heatmapSecondaryWeightField'
    | 'aiDialogueSettingsId'
    | 'postDetailFields'
    | 'postDetailScores'
    | 'scoreXKey'
    | 'scoreYKey'
    | 'scoreXLabel'
    | 'scoreYLabel'
    | 'scoreXMin'
    | 'scoreXMax'
    | 'scoreYMin'
    | 'scoreYMax'
    | 'scoreQuadrantTopRight'
    | 'scoreQuadrantTopLeft'
    | 'scoreQuadrantBottomRight'
    | 'scoreQuadrantBottomLeft'
  >;
  jsonText: string;
} {
  const rest: AnalysisDisplayRules = JSON.parse(JSON.stringify(rules));
  // form 担当 field を除外
  if (rest.matrix) {
    delete rest.matrix.defaultVAxis;
    // MIG-1 M-A / M-B: 専用 form に移管した field は JSON 側からは除外。
    delete rest.matrix.scoreFilter;
    delete rest.matrix.imageSize;
    delete rest.matrix.showTitle;
    delete rest.matrix.sortField;
    delete rest.matrix.sortOrder;
    // AW1: 軸ルール系も form 担当に。
    delete rest.matrix.defaultHAxis;
    delete rest.matrix.vAxisRule;
    delete rest.matrix.hAxisRule;
    delete rest.matrix.additionalAxisRules;
    if (Object.keys(rest.matrix).length === 0) delete rest.matrix;
  }
  if (rest.map) {
    delete rest.map.defaultColorAxis;
    // AW1: map 既定も form 担当に。
    delete rest.map.defaultSizeAxis;
    delete rest.map.defaultTileType;
    delete rest.map.defaultMarkerMode;
    delete rest.map.defaultHeatmapMode;
    if (Object.keys(rest.map).length === 0) delete rest.map;
  }
  if (rest.heatmap) {
    delete rest.heatmap.defaultWeightMode;
    delete rest.heatmap.weightField;
    delete rest.heatmap.weightFormula;
    delete rest.heatmap.secondaryWeightField;
    if (Object.keys(rest.heatmap).length === 0) delete rest.heatmap;
  }
  // Task D: score は専用 form 編集に任せて JSON 側からは除外
  delete rest.score;
  delete rest.aiDialogueSettingsId;
  // 動的管理拡張: postDetailFields は専用 form 編集に任せて JSON 側からは除外
  delete rest.postDetailFields;
  // AW1: postDetailScores も専用 form 編集に。
  delete rest.postDetailScores;

  return {
    form: {
      matrixDefaultVAxis: rules.matrix?.defaultVAxis ?? '',
      matrixDefaultHAxis: rules.matrix?.defaultHAxis ?? '',
      matrixVAxisRule: rules.matrix?.vAxisRule ?? null,
      matrixHAxisRule: rules.matrix?.hAxisRule ?? null,
      matrixAdditionalAxisRules: rules.matrix?.additionalAxisRules
        ? [...rules.matrix.additionalAxisRules]
        : [],
      mapDefaultSizeAxis: rules.map?.defaultSizeAxis ?? '',
      mapDefaultTileType: rules.map?.defaultTileType ?? '',
      mapDefaultMarkerMode:
        rules.map?.defaultMarkerMode === undefined
          ? ''
          : rules.map.defaultMarkerMode
            ? 'true'
            : 'false',
      mapDefaultHeatmapMode:
        rules.map?.defaultHeatmapMode === undefined
          ? ''
          : rules.map.defaultHeatmapMode
            ? 'true'
            : 'false',
      matrixScoreFilter: rules.matrix?.scoreFilter ? rules.matrix.scoreFilter.join(',') : '',
      matrixImageSize: rules.matrix?.imageSize !== undefined ? String(rules.matrix.imageSize) : '',
      matrixShowTitle:
        rules.matrix?.showTitle === undefined ? '' : rules.matrix.showTitle ? 'true' : 'false',
      matrixSortField: rules.matrix?.sortField ?? '',
      matrixSortOrder: rules.matrix?.sortOrder ?? '',
      mapDefaultColorAxis: rules.map?.defaultColorAxis ?? '',
      heatmapDefaultWeightMode: rules.heatmap?.defaultWeightMode ?? '',
      heatmapWeightField: rules.heatmap?.weightField ?? '',
      heatmapWeightFormula: rules.heatmap?.weightFormula ?? '',
      heatmapSecondaryWeightField: rules.heatmap?.secondaryWeightField ?? '',
      aiDialogueSettingsId: rules.aiDialogueSettingsId ?? '',
      postDetailFields: rules.postDetailFields ? [...rules.postDetailFields] : [],
      postDetailScores: rules.postDetailScores ?? null,
      scoreXKey: rules.score?.xKey ?? '',
      scoreYKey: rules.score?.yKey ?? '',
      scoreXLabel: rules.score?.xLabel ?? '',
      scoreYLabel: rules.score?.yLabel ?? '',
      scoreXMin: rules.score?.xMin !== undefined ? String(rules.score.xMin) : '',
      scoreXMax: rules.score?.xMax !== undefined ? String(rules.score.xMax) : '',
      scoreYMin: rules.score?.yMin !== undefined ? String(rules.score.yMin) : '',
      scoreYMax: rules.score?.yMax !== undefined ? String(rules.score.yMax) : '',
      scoreQuadrantTopRight: rules.score?.quadrantColors?.topRight ?? '',
      scoreQuadrantTopLeft: rules.score?.quadrantColors?.topLeft ?? '',
      scoreQuadrantBottomRight: rules.score?.quadrantColors?.bottomRight ?? '',
      scoreQuadrantBottomLeft: rules.score?.quadrantColors?.bottomLeft ?? '',
    },
    jsonText: Object.keys(rest).length > 0 ? JSON.stringify(rest, null, 2) : '',
  };
}

/**
 * form + JSON textarea → AnalysisDisplayRules への合成。
 * JSON 側を base に form 4 field を上書き (form が source of truth)。
 */
function draftToRules(draft: FormDraft): { rules: AnalysisDisplayRules; jsonError: string | null } {
  let base: AnalysisDisplayRules = {};
  if (draft.jsonText.trim()) {
    try {
      const parsed = JSON.parse(draft.jsonText);
      base = parseAnalysisDisplayRules(parsed);
    } catch (e) {
      return { rules: base, jsonError: `JSON が不正: ${(e as Error).message}` };
    }
  }

  const v = draft.matrixDefaultVAxis.trim();
  if (v) {
    base.matrix = { ...(base.matrix ?? {}), defaultVAxis: v };
  }
  // AW1: defaultHAxis + 軸ルール (vAxisRule / hAxisRule / additionalAxisRules)。
  const h = draft.matrixDefaultHAxis.trim();
  if (h) {
    base.matrix = { ...(base.matrix ?? {}), defaultHAxis: h };
  }
  const cleanRule = (r: AnalysisAxisRule | null): AnalysisAxisRule | null => {
    if (!r || !r.axisKey.trim()) return null;
    const out: AnalysisAxisRule = { axisKey: r.axisKey.trim() };
    if (r.bucketMethod) out.bucketMethod = r.bucketMethod;
    if (r.bucketMethod === 'custom-ranges' && r.customRanges && r.customRanges.length > 0) {
      out.customRanges = r.customRanges;
    }
    return out;
  };
  const vRule = cleanRule(draft.matrixVAxisRule);
  if (vRule) base.matrix = { ...(base.matrix ?? {}), vAxisRule: vRule };
  const hRule = cleanRule(draft.matrixHAxisRule);
  if (hRule) base.matrix = { ...(base.matrix ?? {}), hAxisRule: hRule };
  const addRules = draft.matrixAdditionalAxisRules
    .map(cleanRule)
    .filter((r): r is AnalysisAxisRule => r !== null);
  if (addRules.length > 0) {
    base.matrix = { ...(base.matrix ?? {}), additionalAxisRules: addRules };
  }

  // MIG-1 M-A: scoreFilter (カンマ区切り → number[]、不正値は drop)。
  const sf = draft.matrixScoreFilter.trim();
  if (sf) {
    const parsed = sf
      .split(',')
      .map((s) => s.trim())
      .filter((s) => s !== '')
      .map((s) => Number(s))
      .filter((n) => Number.isFinite(n));
    if (parsed.length > 0) {
      base.matrix = { ...(base.matrix ?? {}), scoreFilter: parsed };
    }
  }
  // MIG-1 M-B: imageSize / showTitle / sortField / sortOrder。
  const imgRaw = draft.matrixImageSize.trim();
  if (imgRaw) {
    const n = Number(imgRaw);
    if (Number.isFinite(n)) {
      base.matrix = { ...(base.matrix ?? {}), imageSize: n };
    }
  }
  if (draft.matrixShowTitle) {
    base.matrix = {
      ...(base.matrix ?? {}),
      showTitle: draft.matrixShowTitle === 'true',
    };
  }
  const sortField = draft.matrixSortField.trim();
  if (sortField) {
    base.matrix = { ...(base.matrix ?? {}), sortField };
  }
  if (draft.matrixSortOrder) {
    base.matrix = { ...(base.matrix ?? {}), sortOrder: draft.matrixSortOrder };
  }

  const c = draft.mapDefaultColorAxis.trim();
  if (c) {
    base.map = { ...(base.map ?? {}), defaultColorAxis: c };
  }
  // AW1: map 既定 (sizeAxis / tileType / markerMode / heatmapMode)。
  const sizeAxis = draft.mapDefaultSizeAxis.trim();
  if (sizeAxis) {
    base.map = { ...(base.map ?? {}), defaultSizeAxis: sizeAxis };
  }
  if (draft.mapDefaultTileType) {
    base.map = { ...(base.map ?? {}), defaultTileType: draft.mapDefaultTileType };
  }
  if (draft.mapDefaultMarkerMode) {
    base.map = { ...(base.map ?? {}), defaultMarkerMode: draft.mapDefaultMarkerMode === 'true' };
  }
  if (draft.mapDefaultHeatmapMode) {
    base.map = { ...(base.map ?? {}), defaultHeatmapMode: draft.mapDefaultHeatmapMode === 'true' };
  }

  if (draft.heatmapDefaultWeightMode) {
    base.heatmap = {
      ...(base.heatmap ?? {}),
      defaultWeightMode: draft.heatmapDefaultWeightMode,
    };
  }
  const wf = draft.heatmapWeightField.trim();
  if (wf) {
    base.heatmap = { ...(base.heatmap ?? {}), weightField: wf };
  }
  const wForm = draft.heatmapWeightFormula.trim();
  if (wForm) {
    base.heatmap = { ...(base.heatmap ?? {}), weightFormula: wForm };
  }
  // reviewer follow-up ⚠️-5: secondaryWeightField を form 側から書込 (空文字は未指定で fallback)。
  const wSec = draft.heatmapSecondaryWeightField.trim();
  if (wSec) {
    base.heatmap = { ...(base.heatmap ?? {}), secondaryWeightField: wSec };
  }

  // Task D 動的化: score sub-rule を form 側 source of truth として上書き
  const scoreXKey = draft.scoreXKey.trim();
  const scoreYKey = draft.scoreYKey.trim();
  const scoreXLabel = draft.scoreXLabel.trim();
  const scoreYLabel = draft.scoreYLabel.trim();
  const tr = draft.scoreQuadrantTopRight.trim();
  const tl = draft.scoreQuadrantTopLeft.trim();
  const br = draft.scoreQuadrantBottomRight.trim();
  const bl = draft.scoreQuadrantBottomLeft.trim();
  const quadrantColors: Record<string, string> = {};
  if (tr) quadrantColors.topRight = tr;
  if (tl) quadrantColors.topLeft = tl;
  if (br) quadrantColors.bottomRight = br;
  if (bl) quadrantColors.bottomLeft = bl;
  const score: Record<string, unknown> = {};
  if (scoreXKey) score.xKey = scoreXKey;
  if (scoreYKey) score.yKey = scoreYKey;
  if (scoreXLabel) score.xLabel = scoreXLabel;
  if (scoreYLabel) score.yLabel = scoreYLabel;
  // reviewer follow-up ⚠️-3: 値域 4 field (空文字なら未指定、parse 失敗時は無視)。
  const parseNum = (s: string): number | undefined => {
    const t = s.trim();
    if (!t) return undefined;
    const n = Number(t);
    return Number.isFinite(n) ? n : undefined;
  };
  const xMin = parseNum(draft.scoreXMin);
  if (xMin !== undefined) score.xMin = xMin;
  const xMax = parseNum(draft.scoreXMax);
  if (xMax !== undefined) score.xMax = xMax;
  const yMin = parseNum(draft.scoreYMin);
  if (yMin !== undefined) score.yMin = yMin;
  const yMax = parseNum(draft.scoreYMax);
  if (yMax !== undefined) score.yMax = yMax;
  if (Object.keys(quadrantColors).length > 0) score.quadrantColors = quadrantColors;
  if (Object.keys(score).length > 0) {
    // 型安全化: parseAnalysisDisplayRules で sanitize される最終段階を経由する想定
    (base as { score?: unknown }).score = score;
  }

  const dlg = draft.aiDialogueSettingsId.trim();
  if (dlg) base.aiDialogueSettingsId = dlg;

  // 動的管理拡張: postDetailFields は form 側を source of truth として上書き (空配列は書込まない)
  if (draft.postDetailFields.length > 0) {
    base.postDetailFields = draft.postDetailFields;
  }

  // AW1: postDetailScores を form 側 source of truth として上書き (空 label 列は drop)。
  if (draft.postDetailScores) {
    const cols = draft.postDetailScores.columns.filter((c) => c.label.trim() !== '');
    if (cols.length > 0) {
      (base as { postDetailScores?: unknown }).postDetailScores = {
        enabled: draft.postDetailScores.enabled,
        columns: cols,
      };
    }
  }

  return { rules: parseAnalysisDisplayRules(base), jsonError: null };
}

type DisplayConfigItem = AnalysisDisplayConfig & { id: string };

function validateAnalysisDisplayConfig(
  raw: unknown,
): (AnalysisDisplayConfig & { id: string }) | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.id !== 'string' || !r.id.trim()) return null;
  if (typeof r.displayName !== 'string') return null;
  if (typeof r.mode !== 'string') return null;
  return r as unknown as AnalysisDisplayConfig & { id: string };
}

export function AnalysisDisplayConfigsPage() {
  const { toast } = useToast();
  // ドキュメント ID の重複判定 (アーカイブ済との衝突も検出) のためアーカイブ済も購読する。
  // 一覧に出すのは従来どおり有効な行のみ (activeItems)。
  const state = useAnalysisDisplayConfigs({ includeArchived: true });
  // 使用状況 (経路1): 地区の直接紐付け (District.analysisDisplayConfigId)。
  const districtsState = useDistricts();
  // 使用状況 (経路2): モードプロファイル (M10) の「新規地区への推奨設定」。アーカイブ済からの
  // 推奨も「使用中」として拾う (未使用と誤判定してアーカイブしないため)。
  const modeProfilesState = useModeProfiles({ filterArchived: false });
  const [editing, setEditing] = useState<FormDraft | null>(null);
  const [isNew, setIsNew] = useState(false);
  const [saving, setSaving] = useState(false);
  const [historyTarget, setHistoryTarget] = useState<{ id: string; name: string } | null>(null);
  /**
   * 「別名で保存」直後 (未保存の複製) の複製元表示設定の表示名 (null=複製状態でない)。
   * 複製注記の表示条件に使う。保存 / 別行の編集 / 新規作成でクリアする。
   */
  const [duplicatedFrom, setDuplicatedFrom] = useState<string | null>(null);
  /**
   * 新規作成中のドキュメント ID を管理者が手で編集したか。
   * true の間は表示名を変えても ID を自動生成し直さない (手入力を上書きしないため)。
   */
  const [idManuallyEdited, setIdManuallyEdited] = useState(false);
  /**
   * ドキュメント ID の入力欄に一度でも触れたか (入力 / フォーカスを外す / 保存を試みる)。
   * 触る前の空欄に「未入力」の赤字を出すと開いた瞬間から画面が壊れて見えるため、
   * 未入力エラーの表示条件に使う (不正文字・重複は触った結果なので即時表示する)。
   */
  const [idTouched, setIdTouched] = useState(false);
  // AW1: 軸キー候補 (config.mode の modeTemplate 由来)。datalist で各軸 input に提示。
  const axisOptions = useMemo(() => buildModeAxisOptions(editing?.mode ?? 'ecw'), [editing?.mode]);

  /** 全表示設定 (アーカイブ済込み)。ID 重複判定と取込時の既存判定に使う。 */
  const allItems = useMemo(() => (state.status === 'ready' ? state.items : []), [state]);
  /** 有効な表示設定 (一覧表示・書き出し対象)。 */
  const activeItems = useMemo(() => allItems.filter((it) => !it.archivedAt), [allItems]);
  const archivedCount = allItems.length - activeItems.length;
  /**
   * 新規 id (slug) の衝突判定に使う既存 id 集合。
   * **アーカイブ済も必ず含める** (新規保存は merge:false の全置換なので、アーカイブ済と
   * 同 id で保存すると復元不能な上書きになる)。
   */
  const existingIds = useMemo(() => allItems.map((it) => it.id), [allItems]);

  /** 表示設定 id → 使用状況 (使用地区 / 推奨元モードプロファイル、2 経路を集計)。 */
  const usageByConfig = useMemo(
    () =>
      groupAnalysisDisplayConfigUsage({
        districts: districtsState.status === 'ready' ? districtsState.items : [],
        modeProfiles: modeProfilesState.status === 'ready' ? modeProfilesState.items : [],
      }),
    [districtsState, modeProfilesState],
  );
  /** 使用状況の読込が終わったか (未完了のうちは「未使用」と断定しない)。 */
  const usageReady = districtsState.status === 'ready' && modeProfilesState.status === 'ready';

  async function handleImport(importedItems: (AnalysisDisplayConfig & { id: string })[]) {
    for (const it of importedItems) {
      const existing = allItems.find((x) => x.id === it.id);
      // Sprint BG-MasterIO follow-up — system field を除去
      const { id, ...rest } = stripSystemFields(it);
      await upsertMaster<AnalysisDisplayConfig>({
        collectionName: COLLECTION_NAME,
        id: id as string,
        uid: 'admin-stub',
        isNew: !existing,
        data: rest as Omit<
          AnalysisDisplayConfig,
          'id' | 'createdAt' | 'updatedAt' | 'createdBy' | 'updatedBy'
        >,
      });
    }
  }

  function handleNew() {
    setEditing({ ...EMPTY_DRAFT });
    setIsNew(true);
    setDuplicatedFrom(null);
    setIdManuallyEdited(false);
    setIdTouched(false);
  }

  function handleEdit(item: DisplayConfigItem) {
    const rules = parseAnalysisDisplayRules(item.displayRules);
    const { form, jsonText } = rulesToDraftPair(rules);
    setEditing({
      id: item.id,
      displayName: item.displayName,
      mode: item.mode,
      ...form,
      jsonText,
    });
    setIsNew(false);
    setDuplicatedFrom(null);
    setIdManuallyEdited(false);
    setIdTouched(false);
  }

  function handleCancel() {
    setEditing(null);
    setIsNew(false);
    setDuplicatedFrom(null);
    setIdManuallyEdited(false);
    setIdTouched(false);
  }

  /**
   * 別名保存 (複製) の共通処理: 渡された draft の内容を丸ごと引き継いだ新規表示設定の
   * 下書きを用意する。**この時点では保存しない** (誤操作で意図しない表示設定が増えないよう、
   * 確定は「保存」ボタンに委ねる)。
   */
  function applyDuplicate(source: FormDraft) {
    setEditing(duplicateDraft(source, existingIds));
    setIsNew(true);
    setDuplicatedFrom(source.displayName);
    setIdManuallyEdited(false); // 複製直後は `{元 id}-copy`。手入力すればそこで固定される
    setIdTouched(false);
    toast(
      'success',
      `「${source.displayName}」の内容を複製しました。表示名称とドキュメント ID を確認して「保存」を押してください。`,
    );
  }

  /** 編集中の表示設定を別名で保存する (未保存の編集内容も含めて引き継ぐ)。 */
  function handleDuplicateCurrent() {
    if (!editing) return;
    applyDuplicate(editing);
  }

  /** 一覧の行から複製する (その行を読み込んだうえで別名保存と同じ状態にする)。 */
  function handleDuplicateFromRow(item: DisplayConfigItem) {
    const rules = parseAnalysisDisplayRules(item.displayRules);
    const { form, jsonText } = rulesToDraftPair(rules);
    applyDuplicate({
      id: item.id,
      displayName: item.displayName,
      mode: item.mode,
      ...form,
      jsonText,
    });
  }

  /**
   * 表示名変更: 新規時かつ ID 未編集なら id を slug 自動生成する。
   * 管理者が ID を手入力済み (idManuallyEdited) のときは、その ID を上書きしない。
   */
  function setDisplayName(name: string) {
    if (!editing) return;
    if (isNew && !idManuallyEdited) {
      setEditing({ ...editing, displayName: name, id: uniqueSlug(name, existingIds) });
    } else {
      setEditing({ ...editing, displayName: name });
    }
  }

  /** ドキュメント ID の手入力 (新規時のみ)。以後は表示名変更で自動生成し直さない。 */
  function setDocumentId(id: string) {
    if (!editing) return;
    setIdManuallyEdited(true);
    setIdTouched(true);
    setEditing({ ...editing, id });
  }

  /** ドキュメント ID を表示名からの自動生成に戻す。 */
  function regenerateDocumentId() {
    if (!editing) return;
    setIdManuallyEdited(false);
    setEditing({ ...editing, id: uniqueSlug(editing.displayName, existingIds) });
  }

  /**
   * 新規作成中のドキュメント ID の問題 (未入力 / 使用不可文字 / 既存重複)。
   * 保存の可否判定に使う (最終防御は handleSave 側)。
   */
  const idError = useMemo(
    () => (editing && isNew ? validateDocumentId(editing.id, existingIds, '分析表示設定') : null),
    [editing, isNew, existingIds],
  );

  /**
   * 入力欄の下に赤字で出すエラー。
   *  - 使用できない文字 / ハイフンの位置 / 既存との重複 = 入力した結果なので即時表示する。
   *  - 未入力 = 一度も触っていない間は出さない (開いた直後から赤字だと壊れて見えるため)。
   */
  const visibleIdError = useMemo(() => {
    if (idError == null) return null;
    if (!idTouched && !(editing?.id ?? '').trim()) return null;
    return idError;
  }, [idError, idTouched, editing]);

  async function handleSave() {
    if (!editing) return;
    if (!editing.displayName.trim()) {
      toast('error', '表示名称 (displayName) は必須です');
      return;
    }
    if (isNew) {
      // 最終防御 (入力欄の赤字表示と同じ判定)。アーカイブ済との重複もここで弾く。
      setIdTouched(true);
      const idErr = validateDocumentId(editing.id, existingIds, '分析表示設定');
      if (idErr) {
        toast('error', idErr);
        return;
      }
    }
    const { rules, jsonError } = draftToRules(editing);
    if (jsonError) {
      toast('error', jsonError);
      return;
    }
    setSaving(true);
    try {
      await upsertMaster<AnalysisDisplayConfig>({
        collectionName: COLLECTION_NAME,
        id: editing.id || undefined,
        uid: 'admin-stub', // Phase 5 で実 master role uid に置換
        isNew,
        data: {
          mode: editing.mode,
          displayName: editing.displayName.trim(),
          displayRules: rules,
          enabled: true,
          version: 1,
          schemaVersion: 1,
          archivedAt: null,
        },
      });
      toast('success', isNew ? '新規作成しました' : '更新しました');
      handleCancel();
    } catch (e) {
      toast('error', `保存失敗: ${(e as Error).message}`);
    } finally {
      setSaving(false);
    }
  }

  async function handleArchive(id: string, name: string) {
    if (!confirm(`「${name}」をアーカイブしますか? (物理削除ではなく archivedAt 設定)`)) return;
    try {
      await archiveMaster(COLLECTION_NAME, id, 'admin-stub');
      toast('success', 'アーカイブしました');
    } catch (e) {
      toast('error', `アーカイブ失敗: ${(e as Error).message}`);
    }
  }

  return (
    <div>
      <header className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900">分析表示設定マスター</h1>
        <p className="text-sm text-slate-500 mt-1">
          分析画面の見た目テンプレ (内部 ID: analysisDisplayConfigs、docs/04 §3-1 M5)。Sprint AU で
          displayRules を型化、必須 4 field を form 編集可能化。
        </p>
      </header>

      <div className="mb-4 flex flex-wrap gap-2 items-center">
        <Button variant="primary" size="md" onClick={handleNew} disabled={editing !== null}>
          ＋ 新規作成
        </Button>
        <MasterImportExportButtons<AnalysisDisplayConfig & { id: string }>
          collectionName={COLLECTION_NAME}
          items={activeItems}
          validator={validateAnalysisDisplayConfig}
          onImport={handleImport}
          disabled={editing !== null}
          expectedMode={['ecw', 'bousai', 'walker', 'kids-bousai']}
        />
      </div>

      {state.status === 'loading' && <p className="text-slate-500">読込中…</p>}
      {state.status === 'error' && (
        <p className="text-danger-600">読込エラー: {state.error.message}</p>
      )}
      {state.status === 'ready' && (
        <>
          <p className="text-xs text-slate-500 mb-3">
            件数: {activeItems.length} (アーカイブ済除外)
            {archivedCount > 0 && (
              <span className="ml-2">
                ／ アーカイブ済 {archivedCount} 件（ドキュメント ID の重複判定に含めます）
              </span>
            )}
          </p>
          <ul className="grid grid-cols-1 gap-2 mb-6">
            {activeItems.map((item) => {
              const usage = usageByConfig[item.id];
              const unused = usageReady && !usage;
              return (
                <li key={item.id}>
                  <Card padding="md" className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-[260px] flex-1">
                      <div>
                        <strong className="text-slate-900">{item.displayName}</strong>
                        <span className="ml-2 text-xs text-slate-500">id: {item.id}</span>
                        <span className="ml-2 text-xs text-slate-500">
                          mode: {getModeLabel(item.mode)} ({item.mode})
                        </span>
                        {unused && (
                          <span
                            className="ml-2 inline-block px-1.5 py-0.5 text-[11px] bg-amber-50 border border-amber-200 text-amber-800 rounded"
                            title="どの地区からも、どのモードプロファイルの推奨設定からも参照されていません。アーカイブしても表示への影響はありません。"
                          >
                            どこからも未使用
                          </span>
                        )}
                      </div>
                      <DisplayConfigUsageSummary
                        usage={usage}
                        ready={usageReady}
                        configMode={item.mode}
                      />
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      <RowExportButton collectionName={COLLECTION_NAME} item={item} />
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => handleEdit(item)}
                        aria-label={`${item.displayName} を編集`}
                      >
                        編集
                      </Button>
                      {/* アーカイブ済は一覧に出さないため、複製は常に有効な行だけに出る。 */}
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => handleDuplicateFromRow(item)}
                        aria-label={`${item.displayName} を複製`}
                        title="この分析表示設定の内容を引き継いだ新しい表示設定の下書きを作ります（この操作では保存されません）"
                      >
                        📋 複製
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => setHistoryTarget({ id: item.id, name: item.displayName })}
                        aria-label={`${item.displayName} の履歴を表示`}
                      >
                        履歴
                      </Button>
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => void handleArchive(item.id, item.displayName)}
                        aria-label={`${item.displayName} をアーカイブ`}
                      >
                        アーカイブ
                      </Button>
                    </div>
                  </Card>
                </li>
              );
            })}
          </ul>
        </>
      )}

      {historyTarget && (
        <MasterHistoryView
          collectionName={COLLECTION_NAME}
          masterId={historyTarget.id}
          masterDisplayName={historyTarget.name}
          onClose={() => setHistoryTarget(null)}
        />
      )}

      {editing && (
        <Card padding="lg" className="border-primary-300">
          <h2 className="text-lg font-bold mb-3">{isNew ? '新規作成' : '編集'}</h2>
          {duplicatedFrom != null && (
            <div className="mb-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
              <p className="font-bold">
                「{duplicatedFrom}」を複製した、まだ保存していない分析表示設定です。
              </p>
              <p className="mt-1">
                表示名称とドキュメント ID
                を確認して「保存」を押すと、新しい分析表示設定として作成されます。ここでの編集は
                複製元には影響しません。
              </p>
            </div>
          )}
          <div className="grid grid-cols-1 gap-3">
            <div>
              <label className="block text-xs text-slate-600 mb-1" htmlFor="adc-name">
                表示名称 (displayName) <span className="text-danger-600">*</span>
              </label>
              <input
                id="adc-name"
                type="text"
                value={editing.displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                className={INPUT_CLASS}
              />
            </div>
            <div>
              <label className="block text-xs text-slate-600 mb-1" htmlFor="adc-id">
                ドキュメント ID (id){' '}
                {isNew ? (
                  <span className="text-danger-600">*</span>
                ) : (
                  <span className="text-slate-400">(変更できません)</span>
                )}
              </label>
              <div className="flex gap-2 items-start">
                <input
                  id="adc-id"
                  type="text"
                  value={editing.id}
                  onChange={(e) => setDocumentId(e.target.value)}
                  onBlur={() => setIdTouched(true)}
                  readOnly={!isNew}
                  aria-invalid={visibleIdError ? true : undefined}
                  className={`${INPUT_CLASS} flex-1 font-mono ${
                    !isNew ? 'bg-slate-100 text-slate-600' : ''
                  }`}
                  placeholder="例: ecw-default-display"
                  spellCheck={false}
                />
                {isNew && idManuallyEdited && (
                  <Button variant="secondary" size="sm" onClick={regenerateDocumentId}>
                    表示名から自動生成
                  </Button>
                )}
              </div>
              {isNew ? (
                <>
                  {visibleIdError && (
                    <p className="mt-1 text-[11px] text-danger-600">{visibleIdError}</p>
                  )}
                  <p className="mt-1 text-[11px] text-slate-500 leading-relaxed">
                    半角の英小文字・数字・ハイフン (-) が使えます (例:
                    ecw-default-display)。表示名称から自動で作られるので、決めたい ID
                    があるときだけ書き換えてください。
                    <span className="font-bold">保存すると後から変更できません</span>
                    （地区とモードプロファイルはこの ID で分析表示設定を参照します）。
                  </p>
                </>
              ) : (
                <p className="mt-1 text-[11px] text-slate-500 leading-relaxed">
                  地区とモードプロファイルが分析表示設定を参照するための ID
                  です。作成後は変更できません。別の ID
                  にしたいときは「別名で保存」で新しい分析表示設定を作り、そちらに ID
                  を付けてください。
                </p>
              )}
            </div>
            <div>
              <label className="block text-xs text-slate-600 mb-1" htmlFor="adc-mode">
                モード (mode)
              </label>
              {/* MD-3 モードドリブン化: 選択肢は ModeProfile (M10) 登記簿から動的生成 (未登録の現在値も表示)。 */}
              <ModeSelectField
                id="adc-mode"
                value={editing.mode}
                onChange={(mode) => setEditing({ ...editing, mode })}
                className={INPUT_CLASS}
              />
            </div>

            <fieldset className="border border-slate-200 rounded-md p-3">
              <legend className="text-xs font-bold text-slate-700 px-1">
                マトリクス表示ルール (displayRules.matrix)
              </legend>

              <div className="grid grid-cols-1 gap-3 mt-2">
                {/* AW1: マトリクス 初期縦軸/横軸 (候補 datalist 付き、手打ちも可) */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <AxisInput
                    id="adc-matrix-vaxis"
                    label="マトリクス 初期縦軸 (matrix.defaultVAxis)"
                    value={editing.matrixDefaultVAxis}
                    onChange={(val) => setEditing({ ...editing, matrixDefaultVAxis: val })}
                    options={axisOptions}
                  />
                  <AxisInput
                    id="adc-matrix-haxis"
                    label="マトリクス 初期横軸 (matrix.defaultHAxis)"
                    value={editing.matrixDefaultHAxis}
                    onChange={(val) => setEditing({ ...editing, matrixDefaultHAxis: val })}
                    options={axisOptions}
                  />
                </div>

                {/* AW1: 軸の分類方法 (bucketMethod / custom-ranges) — old2 renderAxisRule 相当 */}
                <div className="border border-slate-200 rounded-md p-3 bg-white">
                  <p className="text-xs font-bold text-slate-700 mb-2">
                    軸の分類方法 (bucketMethod / カスタム範囲)
                  </p>
                  <p className="text-[11px] text-slate-500 mb-2">
                    指定した軸を何段階に分けるかを制御します。例: Happy/Healthy を{' '}
                    <code>custom-ranges</code> で +3〜-3 の 6 段階に分けるには対象軸に{' '}
                    <code>HappinessScore</code> を選び「カスタム範囲」を選択して「6 段階を生成」。
                  </p>
                  <AxisRuleEditor
                    idPrefix="adc-vrule"
                    label="縦軸ルール (vAxisRule)"
                    rule={editing.matrixVAxisRule}
                    onChange={(r) => setEditing({ ...editing, matrixVAxisRule: r })}
                    options={axisOptions}
                  />
                  <AxisRuleEditor
                    idPrefix="adc-hrule"
                    label="横軸ルール (hAxisRule)"
                    rule={editing.matrixHAxisRule}
                    onChange={(r) => setEditing({ ...editing, matrixHAxisRule: r })}
                    options={axisOptions}
                  />
                  {editing.matrixAdditionalAxisRules.map((r, i) => (
                    <AxisRuleEditor
                      key={i}
                      idPrefix={`adc-arule-${i}`}
                      label={`追加の軸ルール #${i + 1} (軸切替時に適用)`}
                      rule={r}
                      options={axisOptions}
                      onChange={(next) =>
                        setEditing({
                          ...editing,
                          matrixAdditionalAxisRules: next
                            ? editing.matrixAdditionalAxisRules.map((x, idx) =>
                                idx === i ? next : x,
                              )
                            : editing.matrixAdditionalAxisRules.filter((_, idx) => idx !== i),
                        })
                      }
                      onRemove={() =>
                        setEditing({
                          ...editing,
                          matrixAdditionalAxisRules: editing.matrixAdditionalAxisRules.filter(
                            (_, idx) => idx !== i,
                          ),
                        })
                      }
                    />
                  ))}
                  <button
                    type="button"
                    onClick={() =>
                      setEditing({
                        ...editing,
                        matrixAdditionalAxisRules: [
                          ...editing.matrixAdditionalAxisRules,
                          { axisKey: '', bucketMethod: 'auto' },
                        ],
                      })
                    }
                    className="text-[11px] text-primary-600 hover:underline"
                  >
                    ＋ 追加の軸ルールを追加
                  </button>
                </div>

                {/* MIG-1 M-A: matrix.scoreFilter (旧 defaultScoreFilter、0 除外で -3..-1,1..3) */}
                <div>
                  <label
                    className="block text-xs text-slate-600 mb-1"
                    htmlFor="adc-matrix-score-filter"
                  >
                    マトリクス スコアフィルタ (matrix.scoreFilter、カンマ区切り、空文字で全採用)
                  </label>
                  <input
                    id="adc-matrix-score-filter"
                    type="text"
                    value={editing.matrixScoreFilter}
                    onChange={(e) => setEditing({ ...editing, matrixScoreFilter: e.target.value })}
                    className={INPUT_CLASS}
                    placeholder="例: -3,-2,-1,1,2,3 (0 を除外)"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    旧 analysis-display の <code>defaultScoreFilter</code> 相当。 含まれる score
                    値のみマトリクスに描画。
                  </p>
                </div>

                {/* MIG-1 M-B: matrix.imageSize / showTitle / sortField / sortOrder */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label
                      className="block text-xs text-slate-600 mb-1"
                      htmlFor="adc-matrix-image-size"
                    >
                      画像サイズ (matrix.imageSize、px、空で未指定)
                    </label>
                    <input
                      id="adc-matrix-image-size"
                      type="number"
                      step="any"
                      value={editing.matrixImageSize}
                      onChange={(e) => setEditing({ ...editing, matrixImageSize: e.target.value })}
                      className={INPUT_CLASS}
                      placeholder="30"
                    />
                  </div>
                  <div>
                    <label
                      className="block text-xs text-slate-600 mb-1"
                      htmlFor="adc-matrix-show-title"
                    >
                      タイトル表示 (matrix.showTitle)
                    </label>
                    <select
                      id="adc-matrix-show-title"
                      value={editing.matrixShowTitle}
                      onChange={(e) =>
                        setEditing({
                          ...editing,
                          matrixShowTitle: e.target.value as '' | 'true' | 'false',
                        })
                      }
                      className={INPUT_CLASS}
                    >
                      <option value="">(未指定)</option>
                      <option value="true">表示する</option>
                      <option value="false">非表示</option>
                    </select>
                  </div>
                  <div>
                    <label
                      className="block text-xs text-slate-600 mb-1"
                      htmlFor="adc-matrix-sort-field"
                    >
                      sort 軸 (matrix.sortField、例: score, createdAt)
                    </label>
                    <input
                      id="adc-matrix-sort-field"
                      type="text"
                      value={editing.matrixSortField}
                      onChange={(e) => setEditing({ ...editing, matrixSortField: e.target.value })}
                      className={INPUT_CLASS}
                      placeholder="score"
                    />
                  </div>
                  <div>
                    <label
                      className="block text-xs text-slate-600 mb-1"
                      htmlFor="adc-matrix-sort-order"
                    >
                      sort 順 (matrix.sortOrder)
                    </label>
                    <select
                      id="adc-matrix-sort-order"
                      value={editing.matrixSortOrder}
                      onChange={(e) =>
                        setEditing({
                          ...editing,
                          matrixSortOrder: e.target.value as '' | 'asc' | 'desc',
                        })
                      }
                      className={INPUT_CLASS}
                    >
                      <option value="">(未指定)</option>
                      <option value="desc">desc (降順)</option>
                      <option value="asc">asc (昇順)</option>
                    </select>
                  </div>
                </div>

                {/* AW1: 地図 既定 (色軸 / サイズ軸は候補 datalist 付き) */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <AxisInput
                    id="adc-map-color-axis"
                    label="地図 既定の色軸 (map.defaultColorAxis)"
                    value={editing.mapDefaultColorAxis}
                    onChange={(val) => setEditing({ ...editing, mapDefaultColorAxis: val })}
                    options={axisOptions}
                  />
                  <AxisInput
                    id="adc-map-size-axis"
                    label="地図 既定のサイズ軸 (map.defaultSizeAxis)"
                    value={editing.mapDefaultSizeAxis}
                    onChange={(val) => setEditing({ ...editing, mapDefaultSizeAxis: val })}
                    options={axisOptions}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs text-slate-600 mb-1" htmlFor="adc-map-tile">
                      地図 既定タイル (map.defaultTileType)
                    </label>
                    <select
                      id="adc-map-tile"
                      value={editing.mapDefaultTileType}
                      onChange={(e) =>
                        setEditing({
                          ...editing,
                          mapDefaultTileType: e.target.value as '' | 'osm' | 'pmtiles',
                        })
                      }
                      className={INPUT_CLASS}
                    >
                      <option value="">(未指定)</option>
                      <option value="osm">osm (オンライン)</option>
                      <option value="pmtiles">pmtiles (オフライン)</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-slate-600 mb-1" htmlFor="adc-map-marker">
                      マーカー表示 (map.defaultMarkerMode)
                    </label>
                    <select
                      id="adc-map-marker"
                      value={editing.mapDefaultMarkerMode}
                      onChange={(e) =>
                        setEditing({
                          ...editing,
                          mapDefaultMarkerMode: e.target.value as '' | 'true' | 'false',
                        })
                      }
                      className={INPUT_CLASS}
                    >
                      <option value="">(未指定)</option>
                      <option value="true">マーカー</option>
                      <option value="false">クラスタ等</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-slate-600 mb-1" htmlFor="adc-map-heatmap">
                      ヒートマップ既定 (map.defaultHeatmapMode)
                    </label>
                    <select
                      id="adc-map-heatmap"
                      value={editing.mapDefaultHeatmapMode}
                      onChange={(e) =>
                        setEditing({
                          ...editing,
                          mapDefaultHeatmapMode: e.target.value as '' | 'true' | 'false',
                        })
                      }
                      className={INPUT_CLASS}
                    >
                      <option value="">(未指定)</option>
                      <option value="true">ヒートマップ ON</option>
                      <option value="false">OFF</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label
                      className="block text-xs text-slate-600 mb-1"
                      htmlFor="adc-heatmap-weight-mode"
                    >
                      ヒートマップ 既定重みモード (heatmap.defaultWeightMode)
                    </label>
                    <select
                      id="adc-heatmap-weight-mode"
                      value={editing.heatmapDefaultWeightMode}
                      onChange={(e) =>
                        setEditing({
                          ...editing,
                          heatmapDefaultWeightMode: e.target.value as '' | 'count' | 'score',
                        })
                      }
                      className={INPUT_CLASS}
                    >
                      <option value="">(未指定)</option>
                      <option value="count">count</option>
                      <option value="score">score</option>
                    </select>
                  </div>
                  <div>
                    <label
                      className="block text-xs text-slate-600 mb-1"
                      htmlFor="adc-heatmap-weight-field"
                    >
                      ヒートマップ 重みフィールド (heatmap.weightField、score 時のみ)
                    </label>
                    <input
                      id="adc-heatmap-weight-field"
                      type="text"
                      value={editing.heatmapWeightField}
                      onChange={(e) =>
                        setEditing({ ...editing, heatmapWeightField: e.target.value })
                      }
                      className={INPUT_CLASS}
                      placeholder="例: walkable_score"
                    />
                  </div>
                </div>

                {/* Task D 動的化: weightFormula (簡易 evaluator 駆動、weightField より優先) */}
                <div>
                  <label
                    className="block text-xs text-slate-600 mb-1"
                    htmlFor="adc-heatmap-weight-formula"
                  >
                    ヒートマップ重み式 (heatmap.weightFormula、簡易式、例: HappinessScore +
                    HealthScore)
                  </label>
                  <input
                    id="adc-heatmap-weight-formula"
                    type="text"
                    value={editing.heatmapWeightFormula}
                    onChange={(e) =>
                      setEditing({ ...editing, heatmapWeightFormula: e.target.value })
                    }
                    className={INPUT_CLASS}
                    placeholder="例: HappinessScore + HealthScore (空白で区切る 2 オペランド)"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    指定すると weightField/weightAgg より優先。サポートする演算は <code>+</code>{' '}
                    <code>-</code> <code>*</code> <code>/</code> の 1 つで、形は <code>A + B</code>{' '}
                    のように空白区切りの 2 オペランド構成のみ (安全のため eval 不使用)。
                  </p>
                </div>

                {/* reviewer follow-up ⚠️-5: heatmap.secondaryWeightField (product/abs-product 時の二次乗算 field) */}
                <div>
                  <label
                    className="block text-xs text-slate-600 mb-1"
                    htmlFor="adc-heatmap-secondary-weight-field"
                  >
                    補助重みフィールド (heatmap.secondaryWeightField、weightAgg=product 系で primary
                    と乗算)
                  </label>
                  <input
                    id="adc-heatmap-secondary-weight-field"
                    type="text"
                    value={editing.heatmapSecondaryWeightField}
                    onChange={(e) =>
                      setEditing({ ...editing, heatmapSecondaryWeightField: e.target.value })
                    }
                    className={INPUT_CLASS}
                    placeholder="例: HealthScore (空文字なら HealthScore fallback)"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    weightAgg=<code>product</code> / <code>abs-product</code> 時のみ参照。空文字なら{' '}
                    <code>HealthScore</code> fallback (後方互換)。
                  </p>
                </div>

                {/* Task D 動的化: score sub-rule (散布図の軸 / 象限色) */}
                <fieldset className="border border-slate-200 rounded-md p-3 mt-2">
                  <legend className="text-xs font-bold text-slate-700 px-1">
                    散布図 (score、AnalysisScoreView)
                  </legend>
                  <p className="text-xs text-slate-500 mb-2 mt-1">
                    未指定なら HappinessScore × HealthScore で従来動作。
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-slate-600 mb-1" htmlFor="adc-score-xkey">
                        散布図 X 軸キー (score.xKey、extraFields のキー)
                      </label>
                      <input
                        id="adc-score-xkey"
                        type="text"
                        value={editing.scoreXKey}
                        onChange={(e) => setEditing({ ...editing, scoreXKey: e.target.value })}
                        className={INPUT_CLASS}
                        placeholder="例: HappinessScore"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-slate-600 mb-1" htmlFor="adc-score-ykey">
                        散布図 Y 軸キー (score.yKey、extraFields のキー)
                      </label>
                      <input
                        id="adc-score-ykey"
                        type="text"
                        value={editing.scoreYKey}
                        onChange={(e) => setEditing({ ...editing, scoreYKey: e.target.value })}
                        className={INPUT_CLASS}
                        placeholder="例: HealthScore"
                      />
                    </div>
                    <div>
                      <label
                        className="block text-xs text-slate-600 mb-1"
                        htmlFor="adc-score-xlabel"
                      >
                        散布図 X 軸ラベル (score.xLabel)
                      </label>
                      <input
                        id="adc-score-xlabel"
                        type="text"
                        value={editing.scoreXLabel}
                        onChange={(e) => setEditing({ ...editing, scoreXLabel: e.target.value })}
                        className={INPUT_CLASS}
                        placeholder="未指定なら xKey をそのまま表示"
                      />
                    </div>
                    <div>
                      <label
                        className="block text-xs text-slate-600 mb-1"
                        htmlFor="adc-score-ylabel"
                      >
                        散布図 Y 軸ラベル (score.yLabel)
                      </label>
                      <input
                        id="adc-score-ylabel"
                        type="text"
                        value={editing.scoreYLabel}
                        onChange={(e) => setEditing({ ...editing, scoreYLabel: e.target.value })}
                        className={INPUT_CLASS}
                        placeholder="未指定なら yKey をそのまま表示"
                      />
                    </div>
                    {/* reviewer follow-up ⚠️-3: 値域 4 field (空文字なら未指定 fallback で -3..+3) */}
                    <div>
                      <label className="block text-xs text-slate-600 mb-1" htmlFor="adc-score-xmin">
                        散布図 X 軸最小 (score.xMin、空で -3)
                      </label>
                      <input
                        id="adc-score-xmin"
                        type="number"
                        step="any"
                        value={editing.scoreXMin}
                        onChange={(e) => setEditing({ ...editing, scoreXMin: e.target.value })}
                        className={INPUT_CLASS}
                        placeholder="-3"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-slate-600 mb-1" htmlFor="adc-score-xmax">
                        散布図 X 軸最大 (score.xMax、空で +3)
                      </label>
                      <input
                        id="adc-score-xmax"
                        type="number"
                        step="any"
                        value={editing.scoreXMax}
                        onChange={(e) => setEditing({ ...editing, scoreXMax: e.target.value })}
                        className={INPUT_CLASS}
                        placeholder="3"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-slate-600 mb-1" htmlFor="adc-score-ymin">
                        散布図 Y 軸最小 (score.yMin、空で -3)
                      </label>
                      <input
                        id="adc-score-ymin"
                        type="number"
                        step="any"
                        value={editing.scoreYMin}
                        onChange={(e) => setEditing({ ...editing, scoreYMin: e.target.value })}
                        className={INPUT_CLASS}
                        placeholder="-3"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-slate-600 mb-1" htmlFor="adc-score-ymax">
                        散布図 Y 軸最大 (score.yMax、空で +3)
                      </label>
                      <input
                        id="adc-score-ymax"
                        type="number"
                        step="any"
                        value={editing.scoreYMax}
                        onChange={(e) => setEditing({ ...editing, scoreYMax: e.target.value })}
                        className={INPUT_CLASS}
                        placeholder="3"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-slate-600 mb-1" htmlFor="adc-score-qtr">
                        散布図 象限色 右上 (quadrantColors.topRight、#RRGGBB)
                      </label>
                      <input
                        id="adc-score-qtr"
                        type="text"
                        value={editing.scoreQuadrantTopRight}
                        onChange={(e) =>
                          setEditing({ ...editing, scoreQuadrantTopRight: e.target.value })
                        }
                        className={INPUT_CLASS}
                        placeholder="#FF69B4"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-slate-600 mb-1" htmlFor="adc-score-qtl">
                        散布図 象限色 左上 (quadrantColors.topLeft、#RRGGBB)
                      </label>
                      <input
                        id="adc-score-qtl"
                        type="text"
                        value={editing.scoreQuadrantTopLeft}
                        onChange={(e) =>
                          setEditing({ ...editing, scoreQuadrantTopLeft: e.target.value })
                        }
                        className={INPUT_CLASS}
                        placeholder="#00BCD4"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-slate-600 mb-1" htmlFor="adc-score-qbr">
                        散布図 象限色 右下 (quadrantColors.bottomRight、#RRGGBB)
                      </label>
                      <input
                        id="adc-score-qbr"
                        type="text"
                        value={editing.scoreQuadrantBottomRight}
                        onChange={(e) =>
                          setEditing({ ...editing, scoreQuadrantBottomRight: e.target.value })
                        }
                        className={INPUT_CLASS}
                        placeholder="#9C27B0"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-slate-600 mb-1" htmlFor="adc-score-qbl">
                        散布図 象限色 左下 (quadrantColors.bottomLeft、#RRGGBB)
                      </label>
                      <input
                        id="adc-score-qbl"
                        type="text"
                        value={editing.scoreQuadrantBottomLeft}
                        onChange={(e) =>
                          setEditing({ ...editing, scoreQuadrantBottomLeft: e.target.value })
                        }
                        className={INPUT_CLASS}
                        placeholder="#2196F3"
                      />
                    </div>
                  </div>
                </fieldset>

                <div>
                  <label className="block text-xs text-slate-600 mb-1" htmlFor="adc-dialogue-id">
                    対話設定 ID (aiDialogueSettingsId、M4 参照)
                  </label>
                  <input
                    id="adc-dialogue-id"
                    type="text"
                    value={editing.aiDialogueSettingsId}
                    onChange={(e) =>
                      setEditing({ ...editing, aiDialogueSettingsId: e.target.value })
                    }
                    className={INPUT_CLASS}
                    placeholder="例: ecw-dialogue-pilot"
                  />
                </div>
              </div>
            </fieldset>

            {/* AW1 (Critical #2): 投稿詳細スコアカード列 (postDetailScores) 専用 editor */}
            <fieldset className="border border-slate-200 rounded-md p-3">
              <legend className="text-xs font-bold text-slate-700 px-1">
                投稿詳細スコアカード (postDetailScores)
              </legend>
              <div className="mt-2">
                <ScoreColumnsEditor
                  value={editing.postDetailScores}
                  onChange={(next) => setEditing({ ...editing, postDetailScores: next })}
                />
              </div>
            </fieldset>

            {/* 動的管理拡張: postDetailFields 専用 editor (legacy 6 種 + custom ref) */}
            <fieldset className="border border-slate-200 rounded-md p-3">
              <legend className="text-xs font-bold text-slate-700 px-1">
                投稿詳細表示項目 (postDetailFields、PostDetailModal の表示項目、
                {editing.postDetailFields.length} 件)
              </legend>
              <p className="text-xs text-slate-500 mb-2 mt-1">
                空なら canonical default (description / emotions / emotionText / emotionReason /
                categoryCodes / categoryReason) を採用。custom 行は AI 出力 / extraFields /
                aiAnalysisEcw 等の dot-path で動的フィールドを表示できます (例:
                extraFields.HappinessScore、 aggregateScores.overall)。
              </p>
              {editing.postDetailFields.length === 0 ? (
                <p className="text-xs text-slate-500 mb-2">
                  項目がありません。下のボタンで追加 (default 順を使う場合は空のまま保存)。
                </p>
              ) : (
                <ul className="grid grid-cols-1 gap-2 mb-2">
                  {editing.postDetailFields.map((entry, i) => {
                    const isString = typeof entry === 'string';
                    const ref: PostDetailFieldRef =
                      typeof entry === 'string' ? { type: entry } : entry;
                    return (
                      <li
                        key={i}
                        className="flex items-center gap-2 flex-wrap p-2 border border-slate-200 rounded-md bg-slate-50/60"
                      >
                        <select
                          aria-label={`投稿詳細表示項目 ${i + 1} 種別 (type)`}
                          value={ref.type}
                          onChange={(e) => {
                            const nextType = e.target.value as PostDetailFieldRef['type'];
                            const next = [...editing.postDetailFields];
                            // legacy → legacy のときは string で保持 (互換)、custom or label/sourceKey
                            // 指定済みは object として保持
                            if (nextType !== 'custom' && !ref.sourceKey && !ref.label) {
                              next[i] = nextType;
                            } else {
                              next[i] = { ...ref, type: nextType };
                            }
                            setEditing({ ...editing, postDetailFields: next });
                          }}
                          className="px-2 py-1 text-xs border border-slate-300 rounded"
                        >
                          {POST_DETAIL_LEGACY_TYPES.map((t) => (
                            <option key={t} value={t}>
                              {t}
                            </option>
                          ))}
                          <option value="custom">custom</option>
                        </select>
                        {(ref.type === 'custom' || !isString) && (
                          <input
                            type="text"
                            aria-label={`投稿詳細表示項目 ${i + 1} ソースキー (sourceKey)`}
                            value={ref.sourceKey ?? ''}
                            onChange={(e) => {
                              const next = [...editing.postDetailFields];
                              next[i] = { ...ref, sourceKey: e.target.value };
                              setEditing({ ...editing, postDetailFields: next });
                            }}
                            className="flex-1 min-w-[180px] px-2 py-1 text-xs border border-slate-300 rounded font-mono"
                            placeholder={
                              ref.type === 'custom'
                                ? 'sourceKey (例: extraFields.HappinessScore)'
                                : 'sourceKey (任意)'
                            }
                          />
                        )}
                        {(ref.type === 'custom' || !isString) && (
                          <input
                            type="text"
                            aria-label={`投稿詳細表示項目 ${i + 1} 表示名 (label)`}
                            value={ref.label ?? ''}
                            onChange={(e) => {
                              const next = [...editing.postDetailFields];
                              next[i] = { ...ref, label: e.target.value };
                              setEditing({ ...editing, postDetailFields: next });
                            }}
                            className="w-32 px-2 py-1 text-xs border border-slate-300 rounded"
                            placeholder="label (任意)"
                          />
                        )}
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => {
                            if (i === 0) return;
                            const next = [...editing.postDetailFields];
                            const tmp = next[i - 1]!;
                            next[i - 1] = next[i]!;
                            next[i] = tmp;
                            setEditing({ ...editing, postDetailFields: next });
                          }}
                          aria-label={`投稿詳細表示項目 ${i + 1} を上へ`}
                        >
                          ↑
                        </Button>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => {
                            if (i === editing.postDetailFields.length - 1) return;
                            const next = [...editing.postDetailFields];
                            const tmp = next[i + 1]!;
                            next[i + 1] = next[i]!;
                            next[i] = tmp;
                            setEditing({ ...editing, postDetailFields: next });
                          }}
                          aria-label={`投稿詳細表示項目 ${i + 1} を下へ`}
                        >
                          ↓
                        </Button>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => {
                            const next = editing.postDetailFields.filter((_, idx) => idx !== i);
                            setEditing({ ...editing, postDetailFields: next });
                          }}
                          aria-label={`投稿詳細表示項目 ${i + 1} 削除`}
                        >
                          ✕
                        </Button>
                      </li>
                    );
                  })}
                </ul>
              )}
              <div className="flex gap-2 flex-wrap">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() =>
                    setEditing({
                      ...editing,
                      postDetailFields: [...editing.postDetailFields, 'description'],
                    })
                  }
                >
                  ＋ legacy 項目を追加
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() =>
                    setEditing({
                      ...editing,
                      postDetailFields: [
                        ...editing.postDetailFields,
                        { type: 'custom', sourceKey: '', label: '' } as PostDetailFieldRef,
                      ],
                    })
                  }
                >
                  ＋ custom (sourceKey) を追加
                </Button>
              </div>
            </fieldset>

            <div>
              <label className="block text-xs text-slate-600 mb-1" htmlFor="adc-json">
                その他の表示ルール (displayRules JSON、上記 form 4 field 以外を保持)
              </label>
              <textarea
                id="adc-json"
                value={editing.jsonText}
                onChange={(e) => setEditing({ ...editing, jsonText: e.target.value })}
                rows={10}
                className="w-full px-3 py-2 text-xs font-mono border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-400 resize-y"
                placeholder='{ "matrix": { "defaultHAxis": "HEALTHY" }, "map": { "defaultZoom": 14 } }'
                spellCheck={false}
              />
              <p className="text-xs text-slate-500 mt-1">
                空白可。保存時は parseAnalysisDisplayRules で sanitize されます (Phase 5 後続で個別
                form に拡張予定)。
              </p>
            </div>

            <div className="flex justify-end gap-2 mt-2">
              <Button variant="secondary" size="md" onClick={handleCancel} disabled={saving}>
                キャンセル
              </Button>
              {!isNew && (
                <Button
                  variant="secondary"
                  size="md"
                  onClick={handleDuplicateCurrent}
                  disabled={saving}
                  title="いま編集している内容を引き継いで、新しい分析表示設定の下書きを作ります（この操作では保存されません）"
                >
                  別名で保存
                </Button>
              )}
              <Button
                variant="primary"
                size="md"
                onClick={() => void handleSave()}
                disabled={saving || visibleIdError != null}
                title={
                  visibleIdError != null
                    ? 'ドキュメント ID を修正すると保存できます'
                    : '編集内容を保存します'
                }
              >
                {saving ? '保存中...' : '保存'}
              </Button>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}

/**
 * 一覧行に出す「使用地区 / 推奨元モードプロファイル」。
 *
 * 分析表示設定 (M5) は 2 つの経路から参照される:
 *   1. 地区設定の直接紐付け (`District.analysisDisplayConfigId`)
 *   2. モードプロファイル (M10) の「新規地区への推奨設定」(`recommendedMasters.displayConfigId`)。
 *      新規地区作成時にこの値が地区へ転写されるだけで、以後の同期はない (地区は独立して
 *      別の表示設定へ切り替えられる)。
 *
 * 地区の mode と設定の mode が食い違う場合 (別モード地区が流用しているケース) は注記を添える。
 * バッジの見た目は AI 出力フォーマット一覧と共用 (UsageBadges)。
 */
function DisplayConfigUsageSummary(props: {
  usage: DisplayConfigUsage | undefined;
  ready: boolean;
  /** この表示設定自身の mode。地区の mode と食い違う (別モード地区が流用中) 場合に注記する。 */
  configMode: DistrictMode;
}) {
  const { usage, ready, configMode } = props;

  const districtBadges: UsageBadgeItem[] = (usage?.districts ?? []).map((d) => {
    const mismatched = d.mode !== configMode;
    return {
      key: d.id,
      label: d.name,
      note: mismatched ? `モード不一致: ${getModeLabel(d.mode, d.modeLabel)}` : undefined,
      tone: 'district',
      title: mismatched
        ? `この地区 (mode: ${getModeLabel(d.mode, d.modeLabel)}) が、別モード向けのこの分析表示設定を使用しています`
        : 'この地区がこの分析表示設定を使用しています',
    };
  });

  const modeProfileBadges: UsageBadgeItem[] = (usage?.modeProfiles ?? []).map((p) => ({
    key: p.id,
    label: `${p.name} (${getModeLabel(p.mode, p.modeLabel)})`,
    note: p.archived ? 'アーカイブ済み' : undefined,
    tone: p.archived ? 'muted' : 'template',
    title: p.archived
      ? 'アーカイブ済みのモードプロファイルが、この分析表示設定を新規地区への推奨設定にしています'
      : 'このモードプロファイルが、新規地区作成時の推奨設定にしています（新規地区作成時に地区へコピーされます）',
  }));

  return (
    <dl className="mt-2 grid grid-cols-1 gap-1">
      <div className="flex gap-2 items-start">
        <dt className="w-[7rem] shrink-0 text-xs text-slate-500">使用地区</dt>
        <dd className="flex-1">
          <UsageBadges
            items={districtBadges}
            emptyLabel={ready ? '使用中の地区はありません' : '読込中…'}
          />
        </dd>
      </div>
      <div className="flex gap-2 items-start">
        <dt className="w-[7rem] shrink-0 text-xs text-slate-500">推奨元プロファイル</dt>
        <dd className="flex-1">
          <UsageBadges
            items={modeProfileBadges}
            emptyLabel={ready ? 'どのモードプロファイルからも推奨されていません' : '読込中…'}
          />
        </dd>
      </div>
    </dl>
  );
}
